import { useEffect, useRef, useState, type ReactNode } from "react";

import { PositionChip, StrengthBadge } from "./atoms";

/** Quem aparece numa das duas colunas da troca. */
export interface JogadorDaTroca {
  id: string;
  name: string;
  /** A sigla que o chip mostra: a vaga em campo, ou a posição de origem no banco. */
  pos: string;
  strength: number;
  /** Condição física, quando a partida tem esse dado. */
  condition?: number;
}

type Lado = "campo" | "banco";

interface Arrasto {
  lado: Lado;
  id: string;
  nome: string;
  /** Onde o ponteiro desceu, para medir o limiar do arrasto. */
  ox: number;
  oy: number;
  x: number;
  y: number;
  ativo: boolean;
}

/** Quanto o ponteiro anda antes de o toque virar arrasto. */
const LIMIAR_DO_ARRASTO = 6;

/**
 * A substituição da partida ao vivo, com as duas listas lado a lado.
 *
 * Antes era uma lista de cada vez: escolher quem sai trocava a lista pela de
 * quem entra, e não havia como arrastar. Agora campo e banco ficam juntos e a
 * troca sai de dois jeitos: tocar num e depois no outro, em qualquer ordem, ou
 * arrastar de uma coluna para a outra com o mouse. No toque de dedo o arrasto
 * fica de fora, como na tática: a lista rola com o dedo e o toque duplo basta.
 */
export function TrocaNoBanco({
  emCampo,
  banco,
  rotuloDoCampo,
  rotuloDoBanco,
  vazioDoCampo,
  vazioDoBanco,
  bloqueado,
  aoTrocar,
  ordenarBanco,
}: {
  emCampo: JogadorDaTroca[];
  banco: JogadorDaTroca[];
  rotuloDoCampo: string;
  rotuloDoBanco: string;
  vazioDoCampo: string;
  vazioDoBanco: string;
  /** Sem troca possível: sem substituições restando, ou esperando o motor. */
  bloqueado: boolean;
  aoTrocar: (saiId: string, entraId: string) => void;
  /** Ordena o banco por quem cobre melhor a vaga de quem vai sair. */
  ordenarBanco?: (reservas: JogadorDaTroca[], saindo: JogadorDaTroca) => JogadorDaTroca[];
}): ReactNode {
  const [pego, definirPego] = useState<{ lado: Lado; id: string } | null>(null);
  const [arrasto, definirArrasto] = useState<Arrasto | null>(null);
  const arrastouAgora = useRef(false);

  // Mudou quem está em campo (a troca saiu, ou o jogo andou): a seleção caduca.
  // Pelo conteúdo e não pela lista: quem chama remonta as listas a cada
  // renderização, e a partida online renderiza a cada segundo do relógio.
  const quemEstaOnde = `${emCampo.map((jogador) => jogador.id).join()}|${banco.map((jogador) => jogador.id).join()}`;
  useEffect(() => {
    definirPego(null);
  }, [quemEstaOnde]);

  useEffect(() => {
    if (bloqueado) definirPego(null);
  }, [bloqueado]);

  const saindo =
    (pego?.lado === "campo" ? emCampo.find((jogador) => jogador.id === pego.id) : undefined) ??
    (arrasto?.ativo && arrasto.lado === "campo" ? emCampo.find((jogador) => jogador.id === arrasto.id) : undefined);
  const reservas = saindo && ordenarBanco ? ordenarBanco(banco, saindo) : banco;

  function trocar(de: { lado: Lado; id: string }, para: { lado: Lado; id: string }): void {
    if (de.lado === para.lado) return;
    const sai = de.lado === "campo" ? de.id : para.id;
    const entra = de.lado === "banco" ? de.id : para.id;
    definirPego(null);
    aoTrocar(sai, entra);
  }

  function tocar(lado: Lado, id: string): void {
    if (bloqueado) return;
    if (arrastouAgora.current) {
      arrastouAgora.current = false;
      return;
    }
    if (!pego || pego.lado === lado) {
      definirPego(pego?.id === id ? null : { lado, id });
      return;
    }
    trocar(pego, { lado, id });
  }

  // O arrasto mora na janela, porque o ponteiro sai de cima da linha no meio
  // do gesto. O estado mais recente chega por ref, para os ouvintes não lerem
  // o arrasto de quando foram registrados.
  const atual = useRef({ arrasto, trocar });
  atual.current = { arrasto, trocar };

  useEffect(() => {
    if (!arrasto) return;
    const aoMover = (evento: PointerEvent): void => {
      const agora = atual.current.arrasto;
      if (!agora) return;
      const andou = Math.hypot(evento.clientX - agora.ox, evento.clientY - agora.oy) > LIMIAR_DO_ARRASTO;
      definirArrasto({ ...agora, ativo: agora.ativo || andou, x: evento.clientX, y: evento.clientY });
    };
    const aoSoltar = (evento: PointerEvent): void => {
      const agora = atual.current.arrasto;
      definirArrasto(null);
      if (!agora?.ativo) return;
      arrastouAgora.current = true;
      window.setTimeout(() => {
        arrastouAgora.current = false;
      }, 0);
      const alvo = document
        .elementFromPoint(evento.clientX, evento.clientY)
        ?.closest<HTMLElement>("[data-troca-id]");
      const lado = alvo?.dataset.trocaLado as Lado | undefined;
      const id = alvo?.dataset.trocaId;
      if (lado && id) atual.current.trocar({ lado: agora.lado, id: agora.id }, { lado, id });
    };
    const aoCancelar = (): void => definirArrasto(null);
    window.addEventListener("pointermove", aoMover);
    window.addEventListener("pointerup", aoSoltar);
    window.addEventListener("pointercancel", aoCancelar);
    return () => {
      window.removeEventListener("pointermove", aoMover);
      window.removeEventListener("pointerup", aoSoltar);
      window.removeEventListener("pointercancel", aoCancelar);
    };
    // Registra uma vez por gesto: começa quando o arrasto nasce e sai quando ele acaba.
  }, [arrasto === null]);

  const ladoDoGesto = arrasto?.ativo ? arrasto.lado : pego?.lado;

  const coluna = (lado: Lado, rotulo: string, vazio: string, jogadores: JogadorDaTroca[]): ReactNode => (
    <div style={{ minWidth: 0 }}>
      <div className="label-muted" style={{ marginBottom: 6 }}>
        {rotulo}
      </div>
      {jogadores.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {vazio}
        </p>
      ) : (
        <div className="grid" style={{ gap: 4 }}>
          {jogadores.map((jogador) => {
            const escolhido = (pego?.lado === lado && pego.id === jogador.id) || (arrasto?.ativo && arrasto.id === jogador.id);
            const alvo = ladoDoGesto !== undefined && ladoDoGesto !== lado;
            return (
              <button
                key={jogador.id}
                type="button"
                className="ghost"
                data-troca-lado={lado}
                data-troca-id={jogador.id}
                disabled={bloqueado}
                onClick={() => tocar(lado, jogador.id)}
                onPointerDown={(evento) => {
                  if (bloqueado || evento.pointerType === "touch" || evento.button !== 0) return;
                  definirArrasto({
                    lado,
                    id: jogador.id,
                    nome: jogador.name,
                    ox: evento.clientX,
                    oy: evento.clientY,
                    x: evento.clientX,
                    y: evento.clientY,
                    ativo: false,
                  });
                }}
                style={{
                  display: "flex",
                  width: "100%",
                  minWidth: 0,
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  minHeight: 34,
                  padding: "4px 8px",
                  cursor: bloqueado ? undefined : "grab",
                  borderColor: escolhido ? "var(--primary)" : alvo ? "var(--border-strong, var(--border))" : undefined,
                  background: escolhido ? "var(--primary-glow)" : undefined,
                  borderStyle: alvo && !escolhido ? "dashed" : undefined,
                }}
              >
                <PositionChip pos={jogador.pos} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {jogador.name}
                </span>
                {jogador.condition !== undefined && (
                  <span
                    className="num troca-condicao"
                    style={{
                      fontSize: 11,
                      color: jogador.condition < 70 ? "var(--warning)" : "var(--text-dim)",
                    }}
                  >
                    {jogador.condition}%
                  </span>
                )}
                <StrengthBadge value={jogador.strength} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className="troca-no-banco"
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, alignItems: "start" }}
      >
        {coluna("campo", rotuloDoCampo, vazioDoCampo, emCampo)}
        {coluna("banco", rotuloDoBanco, vazioDoBanco, reservas)}
      </div>
      {arrasto?.ativo && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: arrasto.x + 12,
            top: arrasto.y + 8,
            pointerEvents: "none",
            zIndex: 100,
            padding: "4px 10px",
            borderRadius: 6,
            background: "var(--surface-2)",
            border: "1px solid var(--primary)",
            fontSize: 12.5,
            fontWeight: 600,
            boxShadow: "0 6px 18px rgba(0,0,0,.45)",
          }}
        >
          {arrasto.nome}
        </div>
      )}
    </>
  );
}
